import { useState } from 'react'
import { NextRouter } from 'next/router'
import { Trans, useTranslation } from 'next-i18next'
import styled from 'styled-components'
import {
  Box,
  Button,
  Dialog,
  Inline,
  Stack,
  Text,
  TextLink
} from '@machobear/design-system'
import { format } from 'date-fns'
import { Userpilot } from 'userpilot'

import {
  AccountType,
  PlanName,
  usePricingPlans
} from '@/features/Pricing/pricing-plans'
import useSetPlans from '@/features/Pricing/use-set-plans'
import { EquifaxLogo, TimesCircle } from '@/components/icons'

const SvgWrapper = styled.div`
  display: flex;
  align-items: center;
  svg {
    flex-shrink: 0;
  }
`

const EquifaxWrapper = styled.div`
  display: inline-block;
  svg {
    flex-shrink: 0;
    margin-left: 8px;
    margin-bottom: -3px;
  }
`

interface Props {
  visible: boolean
  closeModal: () => void
  currentPlan: PlanName
  targetPlan: AccountType
  currentPlanEndDate: string
  router: NextRouter
}

const UpgradeDowngradeModal: React.FC<Props> = ({
  visible,
  closeModal,
  currentPlan,
  targetPlan,
  currentPlanEndDate,
  router
}) => {
  const { t } = useTranslation('pricing')
  const plansData = usePricingPlans()
  const { handleReplaceIntendedPlan } = useSetPlans(router)
  const currentPlanData = plansData[currentPlan]
  const [submitting, setSubmitting] = useState(false)

  const featuresList = currentPlanData.features

  const onSubmit = async () => {
    setSubmitting(true)
    await handleReplaceIntendedPlan(targetPlan)
    Userpilot.track('app_downgrade_modal_confirm_btn')
    setSubmitting(false)
    closeModal()
  }

  const plans = {
    essentials: t('pricing:Essentials (Free)'),
    growth: t('pricing:Growth'),
    business: t('pricing:Business')
  }
  const planText = plans[targetPlan]
  const billingPeriodDate = currentPlanEndDate
    ? format(new Date(currentPlanEndDate), 'MMM d, yyyy')
    : ''

  return (
    <Dialog
      id="upgrade-downgrade-modal"
      title={t(
        'pricing:Downgrade Modal Title',
        'Are you sure you want to downgrade?'
      )}
      open={visible}
      onClose={closeModal}
    >
      <Box marginTop="xsmall">
        <Stack space="large">
          <Stack space="gutter">
            <Text>
              <Trans i18nKey="pricing:Downgrade Modal Description">
                By downgrading to the <strong>{{ planText }}</strong> plan, you
                will lose access to:
              </Trans>
            </Text>
            <Stack space="xsmall">
              {featuresList.included.map(feature => (
                <Box
                  key={feature.name}
                  display="flex"
                  alignItems="center"
                  gap="xsmall"
                >
                  <SvgWrapper>
                    <TimesCircle width={21} height={21} />
                  </SvgWrapper>
                  <Box display="flex" alignItems="center" gap="xxsmall">
                    <Text baseline={false}>
                      {feature.name}
                      {feature.showPoweredBy &&
                        t('pricing:powered by', ', powered by')}
                      {feature.showPoweredBy && (
                        <EquifaxWrapper>
                          <EquifaxLogo width={74} height={14} />
                        </EquifaxWrapper>
                      )}
                    </Text>
                  </Box>
                </Box>
              ))}
            </Stack>
            <Text>
              <Trans i18nKey="pricing:Downgrade Modal Description 2">
                If you proceed, your account will be downgraded at the end of
                your current billing period on{' '}
                <strong>{{ billingPeriodDate }}</strong>. Have any questions?{' '}
                <TextLink
                  href="mailto:support@liv.rent"
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  Contact us
                </TextLink>
              </Trans>
            </Text>
          </Stack>
          <Inline space="small" align="right">
            <Button
              tone="critical"
              onClick={onSubmit}
              disabled={submitting}
              id="landlord-downgrade-modal-continue-click"
            >
              {t('pricing:Confirm Downgrade', 'Confirm Downgrade')}
            </Button>
          </Inline>
        </Stack>
      </Box>
    </Dialog>
  )
}

export default UpgradeDowngradeModal
